import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { finalReportApi, userPanelApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Box, BoxSection } from 'components/_new/Box';
import { useGlobal } from 'contexts/globalContext';
import { usePanel } from 'contexts/panelContext';
import { InputTextArea } from 'components/_new/InputTextArea';
import { Field } from 'components/_new/Field';
import { RouterUrlParams } from 'App';
import { usePair } from 'contexts/pairContext';
import { MessageBox, MessageBoxRef } from 'components/_new/MessageBox';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'components/_new/Button';
import { Page } from 'components/_new/Page';
import { ContentSection } from 'components/_new/Content/ContentSection';
import { ArrowList } from 'components/_new/List';
import { ViewType, ViewTypeDropdown } from 'components/_new/ViewTypeDropdown';
import PairPartnerInfo from '../components/PairPartnerInfo';

type FinalReportForm = {
	goal: Array<any>;
	specific_skills_developed: string;
	mentoring_process_supportive: string;
	general_recomendations_for_development: string;
	mentor_thanks: string;
	additional_notes: string;
	visibility: boolean;
};
type GoalAnswerType = 'achievements' | 'working' | 'recommendation';
type AutosaveFieldName = keyof FinalReportForm | `goal.${number}.${GoalAnswerType}`;

export const FinalRaport = () => {
	const { t } = useTranslation();
	const { organizationName, programName, type } = useParams() as RouterUrlParams;

	const { toastRef } = useGlobal();
	const { currentProgramMembership, currentApplicationData, panelType, todosRefetch } = usePanel();
	const {
		pair: { id: pairId, application },
	} = usePair();

	const { control, handleSubmit, setValue, getValues } = useForm<FinalReportForm>({
		defaultValues: {
			goal: [],
			specific_skills_developed: '',
			mentoring_process_supportive: '',
			general_recomendations_for_development: '',
			mentor_thanks: '',
			additional_notes: '',
			visibility: false,
		},
	});
	useFieldArray({ name: 'goal', control });

	const { data: goalsData, isFetched: goalsFetched } = useQuery(
		['goals', pairId],
		() => userPanelApi.getGoals(pairId, currentProgramMembership.id),
		{
			enabled: Boolean(pairId && currentProgramMembership?.id),
		}
	);

	const [defaultValues, setDefaultValues] = useState<Record<string, string | number>>({});
	const [isAutosave, setIsAutosave] = useState(false);
	const [viewType, setViewType] = useState<ViewType>('simpleView');
	const previousVisibilityRef = useRef<boolean | null>(null);
	const {
		data: finalReportData,
		refetch: finalReportRefetch,
		isFetched,
	} = useQuery(
		['final-report', { membershipId: currentProgramMembership?.id, pairId }],
		() => finalReportApi.getFinalReport(pairId, currentApplicationData?.id),
		{
			enabled: Boolean(goalsFetched),
			refetchOnWindowFocus: false,
			onSuccess: ({ answers, visibleForMentee }: any) => {
				const rawDefaultValues: Record<string, string | number> = {};
				(answers || []).forEach((answer: any) => {
					const value = answer.value ? String(answer.value) : '';
					if (answer?.goalId !== null) {
						setValue(`goal.${answer.goalId}.${answer.type}`, value);
						rawDefaultValues[`goal.${answer.goalId}.${answer.type}`] = value;
					} else {
						setValue(answer.type, value);
						rawDefaultValues[`${answer.type}`] = value;
					}
				});
				setValue('visibility', visibleForMentee);

				rawDefaultValues.visibility = visibleForMentee;
				setDefaultValues(rawDefaultValues);
				previousVisibilityRef.current = visibleForMentee;
			},
		}
	);

	const queryClient = useQueryClient();
	const hasExtendedAnswers = useMemo(() => {
		const answers = finalReportData?.answers || [];
		return answers.some((answer: any) => {
			const value = String(answer?.value || '').trim();
			if (!value) {
				return false;
			}

			if (answer?.goalId !== null) {
				return ['working', 'recommendation'].includes(answer.type);
			}

			return ['mentoring_process_supportive', 'mentor_thanks', 'additional_notes'].includes(answer.type);
		});
	}, [finalReportData?.answers]);
	const hasAnyMenteeMentorSummaryData = useMemo(() => {
		const answers = finalReportData?.answers || [];
		return answers.some((answer: any) => String(answer?.value || '').trim().length > 0);
	}, [finalReportData?.answers]);

	useEffect(() => {
		if (hasExtendedAnswers) {
			setViewType('professionalView');
		}
	}, [hasExtendedAnswers]);

	const { mutate: updateFinalReportMutate, isLoading: updateFinalReportLoading } = useMutation(
		(data: any) => finalReportApi.updateFinalReportInfo(pairId, data),
		{
			onSuccess: () => {
				queryClient.refetchQueries({ queryKey: ['sessions', { pairId }] });
				finalReportRefetch();
				const currentVisibility = getValues('visibility');
				if (previousVisibilityRef.current !== null && previousVisibilityRef.current !== currentVisibility) {
					todosRefetch();
				}
				previousVisibilityRef.current = currentVisibility;

				if (!isAutosave) {
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.finalReport.dataUpdatedSuccessfully'),
					});
				}
			},
		}
	);

	const prepareValues = (values: FinalReportForm) => {
		return Object.fromEntries(
			Object.entries(values).map(([k, v]) => {
				if (k === 'goal') {
					const goalAnswers = (v as Array<any>)
						.map((subanswers: Record<string, string>, index: number) =>
							Object.entries(subanswers).map(([k, v]) => ({ goalId: index, type: k, value: v }))
						)
						.filter(Boolean)
						.flat();
					return [k, goalAnswers];
				}
				return [k, v === undefined ? null : v];
			})
		);
	};

	const handleSubmitForm = handleSubmit((values) => {
		setIsAutosave(false);
		const preparedValues = prepareValues(values);
		updateFinalReportMutate(preparedValues);
	});

	const autosaveMessageRefs = useRef<Record<string, MessageBoxRef>>({});

	const autosave = (fieldName: AutosaveFieldName) => {
		const values = prepareValues(getValues());
		const isGoal = fieldName.split('.')[0] === 'goal';
		if (isGoal) {
			const [goalId, goalType] = fieldName.split('.').slice(1);
			const value =
				((values.goal as Array<{ goalId: number; type: string; value: string }>) || []).find(
					(g) => g.goalId === Number(goalId) && g.type === goalType
				)?.value || '';
			if (value !== defaultValues[fieldName]) {
				updateFinalReportMutate({
					goal: [
						{
							goalId: Number(goalId),
							type: goalType,
							value,
						},
					],
				});
				setDefaultValues((prev) => ({ ...prev, [fieldName]: value }));
				setIsAutosave(true);
				autosaveMessageRefs.current[fieldName]?.show();
			}
		} else {
			const value = values[fieldName];
			if (value !== defaultValues[fieldName]) {
				updateFinalReportMutate({ [fieldName]: value });
				setDefaultValues((prev) => ({ ...prev, [fieldName]: value as string | number }));
				setIsAutosave(true);
				autosaveMessageRefs.current[fieldName]?.show();
			}
		}
	};

	return (
		<>
			<Helmet title={t('userPanel.finalReport.finalReportForMentee')} />
			<SubPageTitle
				title={
					<div className="flex flex-column">
						{t('userPanel.finalReport.finalReportForMentee')}
						<PairPartnerInfo />
					</div>
				}
			/>

			<Page>
				<ContentSection
					title={t('userPanel.finalReport.finalReportForMentee')}
					titleIconProps={{ name: 'clipboard', iconStyle: 'regular' }}
				>
					<p>{t('userPanel.finalReport.message')}</p>
					<MessageBox variant="pink">
						<ArrowList>
							<li>{t('userPanel.finalReport.instruction.first')}</li>
							<li>{t('userPanel.finalReport.instruction.second')}</li>
							<li>
								<Trans
									t={t}
									i18nKey="userPanel.finalReport.instruction.third"
									components={[
										<Link
											to={`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/process-summary`}
											className="p-link"
										/>,
									]}
								/>
							</li>
						</ArrowList>
					</MessageBox>

					{panelType === 'mentee' &&
						(!finalReportData?.visibleForMentee || !hasAnyMenteeMentorSummaryData) && (
							<MessageBox message={t('userPanel.finalReport.notVisible')} variant="rose" highlight />
						)}
				</ContentSection>

				{(panelType === 'mentor' ||
					(panelType === 'mentee' && finalReportData?.visibleForMentee && hasAnyMenteeMentorSummaryData)) && (
					<ContentSection
						title={t('userPanel.finalReport.mentorSummary')}
						titleIconProps={{ name: 'lightbulb ' }}
					>
						<form onSubmit={handleSubmitForm} className="flex flex-column gap-4">
							{/* {panelType === 'mentor' && (
								<p className="my-2">
									{t('userPanel.finalReport.entryMessageForMentor1')}
									<br />
									<br />
									{t('userPanel.finalReport.entryMessageForMentor2')}
									<br />
									{t('userPanel.finalReport.entryMessageForMentor3')}
								</p>
							)}
							{panelType === 'mentee' && (
								<p className="my-2">
									{t('userPanel.finalReport.entryMessageForMentee1')}
									<br />
									<br />
									{t('userPanel.finalReport.entryMessageForMentee2')}
									<br />
									{t('userPanel.finalReport.entryMessageForMentee3')}
								</p>
							)} */}

							{panelType === 'mentor' && (
								<div className="flex justify-content-end">
									<div style={{ width: 200 }}>
										<ViewTypeDropdown
											defaultProfessional={hasExtendedAnswers}
											onChange={(nextViewType) => setViewType(nextViewType)}
										/>
									</div>
								</div>
							)}

							<div className="flex flex-column gap-4">
								{(goalsData?.goals || [])?.map(({ id, goal }: any, index: number) => {
									const goalNum = index + 1;
									const goalFields = [
										{
											name: 'achievements',
											label: t('userPanel.finalReport.menteeAchievments'),
										},
										{
											name: 'working',
											label: t('userPanel.finalReport.menteeWorking'),
										},
										{
											name: 'recommendation',
											label: t('userPanel.finalReport.mentorRecomendation'),
										},
									];

									const visibleGoalFields = goalFields.filter(({ name }) => {
										if (panelType === 'mentor' && viewType === 'simpleView') {
											return name === 'achievements';
										}
										if (panelType === 'mentee') {
											return Boolean(getValues(`goal.${id}.${name}` as any));
										}
										return true;
									});

									if (panelType === 'mentee' && visibleGoalFields.length === 0) {
										return null;
									}

									return (
										<Box key={id} variant="white-bordered" full wrap>
											<BoxSection
												header={t('userPanel.finalReport.goalNo', { index: goalNum, goal })}
												contentClassName="flex flex-column gap-2"
												headerClassName="text-primary text-lg ml-2 mt-2"
											>
												{visibleGoalFields.map(({ name, label }) => (
													<Field key={name} label={label} htmlFor={`goal.${id}.${name}`}>
														<Controller
															control={control}
															name={`goal.${id}.${name}`}
															render={({ field }) =>
																panelType === 'mentee' ? (
																	<div
																		id={`goal.${id}.${name}`}
																		className="input-base disabled"
																		{...field}
																	>
																		{field.value || '-'}
																	</div>
																) : (
																	<>
																		<InputTextArea
																			id={`goal.${id}.${name}`}
																			{...field}
																			autoResize
																			style={{ minHeight: '10rem' }}
																			maxLength={2000}
																			onBlur={() => {
																				autosave(
																					`goal.${id}.${name}` as keyof FinalReportForm
																				);
																			}}
																		/>
																		<MessageBox
																			ref={(el: MessageBoxRef) => {
																				if (el) {
																					autosaveMessageRefs.current[
																						`goal.${id}.${name}`
																					] = el;
																				}
																			}}
																			message={t(
																				'userPanel.sessionsNew.reflection.autosaveMessage'
																			)}
																			variant="success"
																			autoHide
																			autoHideDuration={3000}
																			className="mb-2"
																			tiny
																		/>
																	</>
																)
															}
														/>
													</Field>
												))}
											</BoxSection>
										</Box>
									);
								})}

								<Box variant="white-bordered" full wrap>
									<BoxSection
										header={t('userPanel.finalReport.generalProcessSummary')}
										contentClassName="flex flex-column gap-2"
										headerClassName="text-primary text-lg ml-2 mt-2"
									>
										{(() => {
											const generalFields = [
												{
													name: 'specific_skills_developed',
													label: t('userPanel.finalReport.specificSkillsDevelopment'),
												},
												{
													name: 'mentoring_process_supportive',
													label: t('userPanel.finalReport.mentoringProcessSupportive'),
												},
												{
													name: 'general_recomendations_for_development',
													label: t(
														'userPanel.finalReport.generalRecomendationForMenteeDevelopment'
													),
												},
												{
													name: 'mentor_thanks',
													label: t('userPanel.finalReport.mentorThanks'),
												},
												{
													name: 'additional_notes',
													label: t('userPanel.finalReport.additionalNotes'),
												},
											];

											const visibleGeneralFields = generalFields.filter(({ name }) => {
												if (panelType === 'mentor' && viewType === 'simpleView') {
													return [
														'specific_skills_developed',
														'general_recomendations_for_development',
													].includes(name);
												}
												if (panelType === 'mentee') {
													return Boolean(getValues(name as any));
												}
												return true;
											});

											return visibleGeneralFields.map(({ name, label }) => (
												<Field key={name} label={label} htmlFor={name}>
													<Controller
														control={control}
														name={name as keyof FinalReportForm}
														render={({ field }) =>
															panelType === 'mentee' ? (
																<div
																	id={name}
																	className="input-base disabled"
																	style={{ minHeight: '10rem' }}
																	{...field}
																>
																	{field.value || '-'}
																</div>
															) : (
																<>
																	<InputTextArea
																		id={name}
																		{...(field as any)}
																		autoResize
																		style={{ minHeight: '10rem' }}
																		maxLength={2000}
																		onBlur={() => {
																			autosave(name as keyof FinalReportForm);
																		}}
																	/>
																	<MessageBox
																		ref={(el: MessageBoxRef) => {
																			if (el) {
																				autosaveMessageRefs.current[name] = el;
																			}
																		}}
																		message={t(
																			'userPanel.sessionsNew.reflection.autosaveMessage'
																		)}
																		variant="success"
																		autoHide
																		autoHideDuration={3000}
																		className="mb-2"
																		tiny
																	/>
																</>
															)
														}
													/>
												</Field>
											));
										})()}
									</BoxSection>
								</Box>
							</div>
							{panelType === 'mentor' && (
								<div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
									<div className="flex flex-row align-items-center">
										<span className="bold mr-2 ml-2">
											{t('userPanel.finalReport.visibility.visible')}
										</span>
										<Controller
											control={control}
											name="visibility"
											render={({ field }) => (
												<InputSwitch
													checked={field.value}
													onChange={(event) => {
														const newValue = Boolean(event.value);
														field.onChange(newValue);
													}}
												/>
											)}
										/>
									</div>

									<div className="flex justify-content-end w-full md:w-auto mt-2 md:mt-0">
										<Button submit label={t('actions.save')} loading={updateFinalReportLoading} />
									</div>
								</div>
							)}
						</form>
					</ContentSection>
				)}
			</Page>
		</>
	);
};
